import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { MapPin, Star, ArrowRight } from 'lucide-react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { SponsoredBadge } from '../components/SponsoredBadge';
import { RestaurantDetailModal } from '../components/RestaurantDetailModal';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { safeFirebaseQuery } from '../utils/firebaseHelper';
import { mockDishes } from '../services/mockData';

function CategoryResults({ user }) {
  const { categoryId } = useParams();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const fetchRestaurants = useCallback(async () => {
    try {
      setLoading(true);
      const restaurantsData = await safeFirebaseQuery(
        async () => {
          const q = query(
            collection(db, 'dishes'),
            where('dishType', '==', categoryId),
            orderBy('averageRating', 'desc'),
            limit(20)
          );
          const querySnapshot = await getDocs(q);
          return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        },
        async () => {
          return mockDishes
            .filter(dish => dish.dishType?.toLowerCase() === categoryId?.toLowerCase())
            .sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
            .slice(0, 20);
        },
        'Error fetching restaurants'
      );

      // Transform dishes into restaurant format
      const restaurantMap = {};
      restaurantsData.forEach(dish => {
        const key = dish.restaurantName || 'Unknown';
        if (!restaurantMap[key]) {
          restaurantMap[key] = {
            id: key,
            name: key,
            cuisine: dish.cuisine || 'Restaurant',
            address: dish.location || 'Address not available',
            phone: '+1 (555) 000-0000',
            rating: dish.averageRating || 0,
            reviews: dish.voteCount || 0,
            priceRange: '$$',
            hours: '11:00 AM - 10:00 PM',
            images: [dish.imageUrl || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800'],
            description: dish.description || `Experience the finest ${categoryId} at ${key}.`,
            specialties: [dish.name],
            dishes: [dish]
          };
        } else {
          restaurantMap[key].dishes.push(dish);
          restaurantMap[key].specialties.push(dish.name);
          // Update rating average
          const totalRating = restaurantMap[key].rating * restaurantMap[key].reviews + (dish.averageRating || 0);
          const totalReviews = restaurantMap[key].reviews + (dish.voteCount || 0);
          restaurantMap[key].rating = totalReviews > 0 ? totalRating / totalReviews : restaurantMap[key].rating;
          restaurantMap[key].reviews = totalReviews;
        }
      });

      setRestaurants(Object.values(restaurantMap));
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    fetchRestaurants();
  }, [categoryId, fetchRestaurants]);

  const categoryNames = {
    pizza: 'Pizza',
    burger: 'Burgers',
    sushi: 'Sushi',
    tacos: 'Tacos',
    ramen: 'Ramen',
    pasta: 'Pasta',
  };

  const categoryName = categoryNames[categoryId] || categoryId;

  if (loading) {
    return (
      <div className="container mx-auto px-8 max-w-7xl py-16 text-center">
        <div className="w-1 h-20 bg-border mx-auto mb-8 animate-pulse"></div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="pt-8 pb-12 border-b border-border"
        >
          <div className="w-1 h-12 bg-border mb-6"></div>
          <h1 className="serif text-6xl mb-4">{categoryName}</h1>
          <p className="text-sm text-muted-foreground">
            {restaurants.length} restaurants available for reservation
          </p>
        </motion.div>

        {/* Restaurant List */}
        <div className="py-12 space-y-8">
          {restaurants.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No restaurants found for this category.</p>
            </div>
          ) : (
            restaurants.map((restaurant, index) => (
              <motion.article
                key={restaurant.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => setSelectedRestaurant(restaurant)}
                className="group cursor-pointer pb-8 border-b border-border last:border-0 hover:border-foreground transition-colors"
              >
                <div className="grid grid-cols-12 gap-8">
                  {/* Image */}
                  <div className="col-span-4">
                    <div className="aspect-[4/3] overflow-hidden">
                      <ImageWithFallback
                        src={restaurant.images[0]}
                        alt={restaurant.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="col-span-8 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h2 className="serif text-4xl group-hover:text-muted-foreground transition-colors mb-2">
                            {restaurant.name}
                          </h2>
                          {restaurant.sponsored && (
                            <div className="mb-3">
                              <SponsoredBadge variant="minimal" />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 bg-stone-100 px-3 py-1.5">
                          <Star className="w-3.5 h-3.5 fill-current" />
                          <span className="text-xs">{restaurant.rating.toFixed(1)}</span>
                        </div>
                      </div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                        {restaurant.cuisine} · {restaurant.priceRange}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                        {restaurant.description}
                      </p>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6 text-xs text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5" />
                          <span>{restaurant.address.split(',')[1] || restaurant.address}</span>
                        </div>
                        <span>·</span>
                        <span>{restaurant.reviews} reviews</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest group-hover:gap-3 transition-all">
                        <span>View & Reserve</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))
          )}
        </div>
      </div>

      {/* Restaurant Detail Modal */}
      {selectedRestaurant && (
        <RestaurantDetailModal
          restaurant={selectedRestaurant}
          onClose={() => setSelectedRestaurant(null)}
        />
      )}
    </>
  );
}

export default CategoryResults;
